/**
 * API client for Ticket-related endpoints
 */

import axios, { AxiosInstance } from 'axios';

const API_BASE = (import.meta.env.VITE_API_URL_PRODUCTION as string) || '/api';

export interface Ticket {
  ticketId: string;
  ticketNumber: string;
  title: string;
  description?: string;
  createdOn: string;
  modifiedOn?: string;
  severityCode: number;
  status: string;
  commCellId: string;
  ownerName?: string;
}

export interface TicketListResponse {
  user: string;
  account: string;
  globalId: string;
  commCellCount: number;
  ticketCount: number;
  tickets: Ticket[];
  summary?: {
    needsAttention: number;
    inProgress: number;
    notStarted: number;
  };
}

export class TicketApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = sessionStorage.getItem('ssoAccessToken');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Get tickets for the current user
   */
  async getTickets(email?: string, scope: 'my' | 'org' = 'my'): Promise<TicketListResponse> {
    const response = await this.client.get<TicketListResponse>('/Ticket', {
      params: { email, scope }
    });
    return response.data;
  }

  /**
   * Get full details for a specific ticket
   */
  async getTicketDetails(id: string): Promise<TicketDetails> {
    const response = await this.client.get<TicketDetails>(`/Ticket/${id}`);
    return response.data;
  }

  /**
   * Get activities for a specific ticket
   */
  async getTicketActivities(id: string): Promise<Activity[]> {
    const response = await this.client.get<Activity[]>(`/Ticket/${id}/Activities`);
    return response.data;
  }
}

export interface TicketDetails extends Ticket {
  description: string;
  accountName: string;
  contactName: string;
  ownerName: string;
  productVersion: string;
}

export interface Activity {
  activityId: string;
  subject: string;
  description: string;
  createdOn: string;
  activityType: string;
}

export const ticketApiClient = new TicketApiClient();
